import { http } from "@/api/http";

export interface ApplicationSummary { id: string; company?: string; role_title?: string; location?: string }
export interface Mentor { id: string; alias: string; industry: string; title: string; introduction: string; specialties: string[]; match_score: number; match_reasons: string[]; avatar: null }
export interface MentorSlot { id: string; start_at: string; end_at: string; duration_minutes: number }
export interface MentorAvailability {
  application: ApplicationSummary;
  mentor: Mentor;
  session_types: Array<{ id: string; label: string; description: string }>;
  durations: Array<{ minutes: number; amount: number; currency: string }>;
  timezone: string;
  slots: MentorSlot[];
}
export interface MentorBooking {
  id: string;
  status: string;
  display_status: string;
  category: "upcoming" | "history";
  mentor: { id: string; alias: string };
  application: ApplicationSummary;
  session_type: string;
  start_at: string;
  end_at: string;
  duration_minutes: number;
  amount: number;
  currency: string;
  join_url?: string | null;
  reschedule_requested: boolean;
  cancellation_requested: boolean;
  cancellation_notification_sent: boolean;
}
export interface MentorBookingDashboard {
  bookings: MentorBooking[];
  upcoming: MentorBooking[];
  history: MentorBooking[];
  summary: { upcoming: number; awaiting_setup: number; completed: number; cancelled: number };
}

export async function getMentorMatches(applicationId: string) {
  const response = await http.get<{ application: ApplicationSummary; mentors: Mentor[] }>("/mentors/matches", { params: { application_id: applicationId } });
  return response.data;
}

export async function getMentorAvailability(mentorId: string, applicationId: string) {
  const response = await http.get<MentorAvailability>(`/mentors/${encodeURIComponent(mentorId)}/availability`, { params: { application_id: applicationId } });
  return response.data;
}

export async function createMentorCheckout(payload: Record<string, unknown>) {
  const response = await http.post<{ booking_id: string; checkout_url: string; hold_expires_at: string }>("/mentor-bookings/checkout", payload);
  return response.data;
}

export async function createMentorCheckoutWithMaterials(payload: FormData) {
  const response = await http.post<{ booking_id: string; checkout_url: string; hold_expires_at: string }>(
    "/mentor-bookings/checkout-with-materials",
    payload,
    { headers: { "Content-Type": null } },
  );
  return response.data;
}

export async function getMentorBooking(bookingId: string) {
  const response = await http.get<{ booking: Record<string, unknown> }>(`/mentor-bookings/${encodeURIComponent(bookingId)}`);
  return response.data.booking;
}

export async function getMentorBookings() {
  const response = await http.get<MentorBookingDashboard>("/mentor-bookings");
  return response.data;
}

export async function requestMentorReschedule(bookingId: string) {
  const response = await http.post<{ ok: boolean; request_status: string }>(
    `/mentor-bookings/${encodeURIComponent(bookingId)}/reschedule-request`,
  );
  return response.data;
}

export async function requestMentorCancellation(bookingId: string) {
  const response = await http.post<{ ok: boolean; request_status: string }>(
    `/mentor-bookings/${encodeURIComponent(bookingId)}/cancellation-request`,
  );
  return response.data;
}
